// ShopThanks.jsx — /shop/thanks
// Landing after checkout completes. Context-aware: detects commission-only,
// merch-only, or mixed carts from the shopOrders doc and shows appropriate
// messaging + CTAs for each case.

import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import PanBar from "./components/PanBar";
import { clearCart } from "../../utils/cart";
import { trackPurchase } from "../../utils/analytics";

const COMMISSION_VARIATION_ID = "DEYWQ452SAJBQQVSOAD6MU6T";

const BG = `radial-gradient(ellipse 140% 95% at 50% 0%,
  #1f59db 0%,
  #1a4b9e 30%,
  #0e2e46 70%,
  #081a2a 100%)`;

export default function ShopThanks({ navigate }) {
  const [squareOrderId, setSquareOrderId] = useState(null);
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const oid = sp.get("orderId") || sp.get("transactionId") || null;
      setSquareOrderId(oid);
      clearCart();
      if (oid) trackPurchase(oid, 0, 0); // actual totals come from Square — track the conversion event
      if (oid) {
        const unsub = onSnapshot(doc(db, "shopOrders", oid), (snap) => {
          if (snap.exists()) setOrderData(snap.data());
        });
        return unsub;
      }
    } catch {}
    window.scrollTo(0, 0);
  }, []);

  // Detect what's in the order — check both lineItems and the isCommission flag
  const lineItems = orderData?.lineItems || [];
  const commissionCount = lineItems.filter(li =>
    li.catalogObjectId === COMMISSION_VARIATION_ID || li.variationId === COMMISSION_VARIATION_ID
  ).reduce((sum, li) => sum + (li.quantity || 1), 0);
  const merchCount = lineItems.reduce((sum, li) => sum + (li.quantity || 1), 0) - commissionCount;
  // Fall back to the isCommission flag if lineItems don't have catalog IDs
  const hasCommission = commissionCount > 0 || !!orderData?.isCommission;
  const hasMerch = merchCount > 0;
  const isCommissionOnly = hasCommission && !hasMerch;
  const isMixed = hasCommission && hasMerch;

  return (
    <div style={{
      minHeight: "100vh",
      background: BG,
      fontFamily: "var(--jop-display)",
      color: "var(--jop-cream)",
      padding: "3rem 1.25rem 5rem",
      boxSizing: "border-box",
    }}>
      <div style={{ maxWidth: "560px", margin: "0 auto" }}>
        <div style={{
          background: "#ffffff",
          color: "#1a1a1a",
          borderRadius: "18px",
          padding: "3rem 2rem",
          boxShadow: "0 8px 48px rgba(0,0,0,0.3)",
          textAlign: "center",
        }}>
          <PanBar width={64} height={3} margin="0 auto 1rem" />

          {/* Hero — changes based on cart contents */}
          <div style={{ fontSize: "3.8rem", marginBottom: "0.5rem" }}>
            {isCommissionOnly ? "🎨" : isMixed ? "🎨🥞" : "🥞"}
          </div>
          <h1 style={{
            fontFamily: "var(--jop-script)",
            fontSize: "clamp(2.2rem, 6vw, 2.8rem)",
            color: "#1d52a0",
            fontWeight: 700,
            margin: "0 0 0.75rem",
            lineHeight: 1.1,
          }}>
            {isCommissionOnly
              ? "Commission booked!"
              : isMixed
              ? "Order confirmed!"
              : "Thanks for ordering!"}
          </h1>

          {/* Body copy — context-aware */}
          {isCommissionOnly ? (
            <p style={{ color: "#555", fontSize: "1rem", lineHeight: 1.6, margin: "0 auto 1.5rem", maxWidth: "420px" }}>
              Your custom pancake commission is confirmed. Doctor Dan will hand-draw
              a one-of-a-kind portrait and preserve it as a lasting work of art.
              {commissionCount > 1
                ? ` You've ordered ${commissionCount} commissions — upload a reference photo for each one below.`
                : " Upload your reference photo below to get started."}
            </p>
          ) : isMixed ? (
            <p style={{ color: "#555", fontSize: "1rem", lineHeight: 1.6, margin: "0 auto 1.5rem", maxWidth: "420px" }}>
              Your order's in! Your merch will be packed and shipped shortly — you'll
              get a tracking number when it's on its way.
              {commissionCount > 1
                ? ` You also have ${commissionCount} commissions — upload reference photos below.`
                : " You also have a commission — upload your reference photo below to get started."}
            </p>
          ) : (
            <p style={{ color: "#555", fontSize: "1rem", lineHeight: 1.6, margin: "0 auto 1.5rem", maxWidth: "420px" }}>
              Your order's in. We'll pack it up and ship it out shortly — you'll get
              a tracking number as soon as it's on its way.
            </p>
          )}

          {squareOrderId && (
            <div style={{ color: "#888", fontFamily: "monospace", fontSize: "0.78rem", marginBottom: "1.5rem" }}>
              Order ref: {squareOrderId}
            </div>
          )}

          {/* Commission upload CTA */}
          {hasCommission && squareOrderId && (
            <div style={{
              background: "linear-gradient(135deg, rgba(252,179,31,0.12), rgba(245,118,0,0.08))",
              border: "2px solid #fcb31f",
              borderRadius: "14px",
              padding: "1.25rem 1.5rem",
              marginBottom: "1.5rem",
              textAlign: "center",
            }}>
              <div style={{ fontSize: "1.8rem", marginBottom: "0.4rem" }}>📸</div>
              <div style={{ color: "#1d52a0", fontWeight: 800, fontSize: "1.05rem", marginBottom: "0.5rem" }}>
                {commissionCount > 1
                  ? `Upload reference photos for your ${commissionCount} commissions`
                  : "Upload your reference photo"}
              </div>
              <p style={{ color: "#555", fontSize: "0.88rem", lineHeight: 1.5, margin: "0 0 1rem" }}>
                This is how we know what to create for you. You can also do this later — we sent you an email with the link.
              </p>
              <button
                onClick={() => window.location.href = `/commission/upload?orderId=${squareOrderId}`}
                style={{
                  background: "#fcb31f",
                  color: "#1a0a12",
                  border: "none",
                  borderRadius: "999px",
                  padding: "0.85rem 1.8rem",
                  fontFamily: "var(--jop-display)",
                  fontSize: "1rem",
                  fontWeight: 800,
                  cursor: "pointer",
                  boxShadow: "0 4px 16px rgba(252,179,31,0.35)",
                }}
              >
                Upload Reference Photo →
              </button>
            </div>
          )}

          <div style={{ display: "flex", gap: "0.6rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => (navigate ? navigate("/shop") : (window.location.href = "/shop"))}
              style={{
                background: hasCommission ? "transparent" : "#fcb31f",
                color: hasCommission ? "#1d52a0" : "#1a0a12",
                border: hasCommission ? "1px solid #d0dcea" : "none",
                borderRadius: "999px",
                padding: "0.75rem 1.4rem",
                fontFamily: "var(--jop-display)",
                fontSize: "0.95rem",
                fontWeight: hasCommission ? 700 : 800,
                cursor: "pointer",
                boxShadow: hasCommission ? "none" : "0 4px 16px rgba(252,179,31,0.35)",
              }}
            >
              Keep browsing
            </button>
            <button
              onClick={() => (navigate ? navigate("/") : (window.location.href = "/"))}
              style={{
                background: "transparent",
                color: "#1d52a0",
                border: "1px solid #d0dcea",
                borderRadius: "999px",
                padding: "0.75rem 1.4rem",
                fontFamily: "var(--jop-display)",
                fontSize: "0.95rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Back to thejoyofpancakes.com
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
